/**
 * Single swappable seam for `/api/rtts/*`.
 * Call sites import from here only — mock vs live is selected by isStudioUseMock().
 */
import { isStudioUseMock } from '../config';
import {
  mockAcquire,
  mockCheckout,
  mockCreatePortalSession,
  mockDeleteReel,
  mockEnsureShareLink,
  mockGetCandidates,
  mockGetEntitlement,
  mockGetJob,
  mockGetProfile,
  mockGetReels,
  mockGetVods,
  mockRecordHighlightInterest,
  mockRenameReel,
  mockSubmitFeedback,
  mockGetFeedbackChannel,
  mockSubmitCurate,
  mockSubmitRefine,
  mockSubmitAccept,
  mockSubmitDurationRerender,
  mockGetMusicManifest,
  mockUpdateProfile,
  mockApplyMusicAddon,
  mockRemoveMusicAddon,
  mockApplyAddonMix,
  mockApplyWatermarkOptIn,
  mockStoreVoiceAddon,
  mockStoreMusicAddon,
  mockRemoveVoiceAddon,
} from './mockRttsClient';
import {
  liveAcquire,
  liveCheckout,
  liveCreatePortalSession,
  liveDeleteReel,
  liveEnsureShareLink,
  liveGetCandidates,
  liveGetEntitlement,
  liveGetJob,
  liveGetProfile,
  liveGetReels,
  liveGetVods,
  liveRecordHighlightInterest,
  liveRenameReel,
  liveSubmitFeedback,
  liveGetFeedbackChannel,
  liveSubmitCurate,
  liveSubmitRefine,
  liveSubmitAccept,
  liveSubmitDurationRerender,
  liveGetMusicManifest,
  liveUpdateProfile,
  liveApplyMusicAddon,
  liveRemoveMusicAddon,
  liveApplyAddonMix,
  liveApplyWatermarkOptIn,
  liveStoreVoiceAddon,
  liveStoreMusicAddon,
  liveRemoveVoiceAddon,
} from './liveRttsClient';
import type {
  AcquireSuccess,
  CandidatesSuccess,
  CheckoutSuccess,
  Entitlement,
  HighlightJob,
  PortalSuccess,
  Profile,
  ProfileUpdate,
  RefineClip,
  RefineMusicOptions,
  ReelSummary,
  StudioCheckoutPlan,
  Vod,
} from './types';
import type { MusicTrack } from '../music/types';
import { loadBundledTracks } from '../music/loadTracks';

export type {
  AcquireSuccess,
  CandidatesSuccess,
  CheckoutSuccess,
  CurationCandidate,
  CurationState,
  Entitlement,
  HighlightJob,
  PortalSuccess,
  Profile,
  ProfileUpdate,
  RefineClip,
  RefineMusicOptions,
  ReelSummary,
  StudioCheckoutPlan,
  Vod,
} from './types';
export {
  AcquireError,
  DurationRerenderError,
  InvalidEmailError,
  PortalError,
  TwitchSignInRequiredError,
  isAcquireError,
  isDailyCapacityReachedError,
  isDurationRerenderError,
  isInvalidEmailError,
  isMonthlyCapReachedError,
  isNotAllowlistedError,
  isPortalError,
  isReelAlreadyExistsError,
  isGenerationCeilingError,
  isGenerationPausedError,
  isReelNameRequiredError,
  isTwitchSignInRequiredError,
  isUnsupportedGameError,
  isUpgradeRequiredError,
} from './types';

/** GET /api/rtts/twitch/vods */
export function getVods(): Promise<Vod[]> {
  return isStudioUseMock() ? mockGetVods() : liveGetVods();
}

/** GET /api/rtts/me/entitlement */
export function getEntitlement(): Promise<Entitlement> {
  return isStudioUseMock() ? mockGetEntitlement() : liveGetEntitlement();
}

/**
 * POST /api/rtts/highlights/acquire
 * Pass the selected VOD's `vod_id` as `twitch_vod_id`.
 * Pass `{ recut: true }` for an explicit metered re-generation.
 */
export function acquire(
  twitchVodId: string,
  options?: {
    recut?: boolean;
    reelName?: string;
    force?: boolean;
    durationTier?: string;
  }
): Promise<AcquireSuccess> {
  return isStudioUseMock()
    ? mockAcquire(twitchVodId, options)
    : liveAcquire(twitchVodId, options);
}

/** GET /api/rtts/highlights/jobs/{id} */
export function getJob(id: string): Promise<{ job: HighlightJob }> {
  return isStudioUseMock() ? mockGetJob(id) : liveGetJob(id);
}

/** GET /api/rtts/highlights/jobs/{id}/candidates */
export function getCandidates(jobId: string): Promise<CandidatesSuccess> {
  return isStudioUseMock() ? mockGetCandidates(jobId) : liveGetCandidates(jobId);
}

/** POST /api/rtts/highlights/jobs/{id}/curate */
export function submitCurate(
  jobId: string,
  selected: string[],
  reelName?: string
): Promise<{ job: HighlightJob }> {
  return isStudioUseMock()
    ? mockSubmitCurate(jobId, selected, reelName)
    : liveSubmitCurate(jobId, selected, reelName);
}

/** POST /api/rtts/highlights/jobs/{id}/refine — cheap re-render, no dock. */
export function submitRefine(
  jobId: string,
  clips: RefineClip[],
  music?: RefineMusicOptions | null
): Promise<{ job: HighlightJob; docked_reel: boolean }> {
  return isStudioUseMock()
    ? mockSubmitRefine(jobId, clips, music)
    : liveSubmitRefine(jobId, clips, music);
}

/** POST /api/rtts/highlights/jobs/{id}/accept — dock + clean re-render. */
export function submitAccept(
  jobId: string,
  music?: RefineMusicOptions | null
): Promise<{
  job: HighlightJob;
  docked_reel: boolean;
  already_accepted: boolean;
}> {
  return isStudioUseMock()
    ? mockSubmitAccept(jobId, music)
    : liveSubmitAccept(jobId, music);
}

/** POST /api/rtts/highlights/jobs/{id}/rerender-duration — free length change. */
export function submitDurationRerender(
  jobId: string,
  durationTier: string
): Promise<{ job: HighlightJob; docked_reel: boolean }> {
  return isStudioUseMock()
    ? mockSubmitDurationRerender(jobId, durationTier)
    : liveSubmitDurationRerender(jobId, durationTier);
}

/**
 * GET /api/rtts/music/manifest — prefer live catalog; fall back to bundled.
 */
export async function getMusicManifest(): Promise<{ tracks: MusicTrack[] }> {
  if (isStudioUseMock()) {
    return mockGetMusicManifest();
  }
  try {
    return await liveGetMusicManifest();
  } catch {
    return { tracks: loadBundledTracks() };
  }
}

/** POST /api/rtts/me/highlight-interest */
export function recordHighlightInterest(): Promise<{
  recorded: boolean;
  already: boolean;
}> {
  return isStudioUseMock()
    ? mockRecordHighlightInterest()
    : liveRecordHighlightInterest();
}

/** GET /api/rtts/me/feedback */
export function getFeedbackChannel(): Promise<{ mailto: string; channel: string }> {
  return isStudioUseMock() ? mockGetFeedbackChannel() : liveGetFeedbackChannel();
}

/** POST /api/rtts/me/feedback */
export function submitFeedback(
  message: string,
  category = 'general'
): Promise<{ recorded: boolean; feedback_id?: string }> {
  return isStudioUseMock()
    ? mockSubmitFeedback(message, category)
    : liveSubmitFeedback(message, category);
}

/**
 * POST /api/rtts/billing/checkout
 * TODO(seam): confirm RTTS_CHECKOUT_SUCCESS_URL/CANCEL_URL match
 * `/studio/upgrade/success` and `/studio/upgrade/cancel` before enabling billing.
 */
export function checkout(plan: StudioCheckoutPlan): Promise<CheckoutSuccess> {
  return isStudioUseMock() ? mockCheckout(plan) : liveCheckout(plan);
}

/** GET /api/rtts/me/profile */
export function getProfile(): Promise<Profile> {
  return isStudioUseMock() ? mockGetProfile() : liveGetProfile();
}

/** PUT /api/rtts/me/profile */
export function updateProfile(patch: ProfileUpdate): Promise<Profile> {
  return isStudioUseMock() ? mockUpdateProfile(patch) : liveUpdateProfile(patch);
}

/** POST /api/rtts/billing/portal */
export function createPortalSession(): Promise<PortalSuccess> {
  return isStudioUseMock() ? mockCreatePortalSession() : liveCreatePortalSession();
}

/** GET /api/rtts/me/reels */
export function getReels(): Promise<{ reels: ReelSummary[] }> {
  return isStudioUseMock() ? mockGetReels() : liveGetReels();
}

/** POST /api/rtts/me/reels/{jobId}/share */
export function ensureShareLink(jobId: string): Promise<{ share_url: string }> {
  return isStudioUseMock() ? mockEnsureShareLink(jobId) : liveEnsureShareLink(jobId);
}

/** DELETE /api/rtts/me/reels/{jobId} */
export function deleteReel(jobId: string): Promise<void> {
  return isStudioUseMock() ? mockDeleteReel(jobId) : liveDeleteReel(jobId);
}

/** PATCH /api/rtts/me/reels/{jobId}/name */
export function renameReel(
  jobId: string,
  reelName: string
): Promise<ReelSummary> {
  return isStudioUseMock()
    ? mockRenameReel(jobId, reelName)
    : liveRenameReel(jobId, reelName);
}

export type AddonMixPayload = {
  music_id?: string | null;
  music_volume: number;
  reel_audio_volume: number;
  voice_volume?: number;
  clear_music?: boolean;
};

/** POST /api/rtts/me/reels/{jobId}/addons/music — does not burn a generation slot. */
export function applyMusicAddon(
  jobId: string,
  musicId: string,
  mix: { music_volume: number; reel_audio_volume: number } = {
    music_volume: 45,
    reel_audio_volume: 100,
  }
): Promise<{ job: HighlightJob }> {
  return isStudioUseMock()
    ? mockApplyMusicAddon(jobId, musicId, mix)
    : liveApplyMusicAddon(jobId, musicId, mix);
}

/** POST /api/rtts/me/reels/{jobId}/addons/mix */
export function applyAddonMix(
  jobId: string,
  mix: AddonMixPayload
): Promise<{ job: HighlightJob }> {
  return isStudioUseMock()
    ? mockApplyAddonMix(jobId, mix)
    : liveApplyAddonMix(jobId, mix);
}

/** POST /api/rtts/me/reels/{jobId}/addons/voice (multipart) */
export function storeVoiceAddon(
  jobId: string,
  file: Blob,
  opts: { voice_source: 'recorded' | 'uploaded'; rights_ack?: boolean }
): Promise<{ job: HighlightJob }> {
  return isStudioUseMock()
    ? mockStoreVoiceAddon(jobId, file, opts)
    : liveStoreVoiceAddon(jobId, file, opts);
}

/** POST /api/rtts/me/reels/{jobId}/addons/music/upload (multipart) */
export function storeMusicAddon(
  jobId: string,
  file: Blob,
  opts: { rights_ack?: boolean } = {}
): Promise<{ job: HighlightJob }> {
  return isStudioUseMock()
    ? mockStoreMusicAddon(jobId, file, opts)
    : liveStoreMusicAddon(jobId, file, opts);
}

/** DELETE /api/rtts/me/reels/{jobId}/addons/music */
export function removeMusicAddon(
  jobId: string
): Promise<{ job: HighlightJob }> {
  return isStudioUseMock()
    ? mockRemoveMusicAddon(jobId)
    : liveRemoveMusicAddon(jobId);
}

/** DELETE /api/rtts/me/reels/{jobId}/addons/voice */
export function removeVoiceAddon(
  jobId: string
): Promise<{ job: HighlightJob }> {
  return isStudioUseMock()
    ? mockRemoveVoiceAddon(jobId)
    : liveRemoveVoiceAddon(jobId);
}

/** POST /api/rtts/me/reels/{jobId}/addons/watermark */
export function applyWatermarkOptIn(
  jobId: string,
  enabled: boolean
): Promise<{ job: HighlightJob }> {
  return isStudioUseMock()
    ? mockApplyWatermarkOptIn(jobId, enabled)
    : liveApplyWatermarkOptIn(jobId, enabled);
}
